import { useMutation, useQuery } from '@apollo/client';
import { captureMessage } from '@sentry/react';
import useDialog from 'hooks/useDialog';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import usePartnerRequestActions from 'FeatureModules/Partner/Components/PartnerRequests/usePartnerRequestActions';
import { UPDATE_QUESTIONNAIRE_ANSWERS } from 'graphql/secure/mutations/qustionnaire.mutations';
import { IUpdateQuestionnaireAnswersInput } from 'graphql/secure/mutations/types/questionnaire-mutation.types';
import { GET_QUESTIONNAIRE_COMPANY_RESPONSE_OVERVIEW } from 'graphql/secure/queries/questionnaire.queries';
import omit from 'lodash/omit';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EPartnerRequestStatus, ERequestType } from 'FeatureModules/Partner/types/partner.types';
import {
  IBaseQuestionnaire,
  IQuestionnaireCompanyResponse,
  IQuestionnaireFormValues,
} from 'FeatureModules/Questionnaire/types/questionnaire.types';

const useQuestionnaireAnswerForm = () => {
  const { questionnaireId, questionnaireResponseId } = useParams();
  const { openDialog } = useDialog();
  const { updatePartnerRequest } = usePartnerRequestActions();
  const { setErrorMessage, setSuccessMessage } = useMessages();
  const navigate = useNavigate();
  const { logEvent } = useLogEvent();
  const { data, error, loading } = useQuery<{
    questionnaireCompanyResponse: IQuestionnaireCompanyResponse;
    questionnaire: IBaseQuestionnaire;
  }>(GET_QUESTIONNAIRE_COMPANY_RESPONSE_OVERVIEW, {
    variables: { questionnaireId, questionnaireResponseId },
    // The form has an issue where it does not update if the values are not there on first load
    fetchPolicy: 'network-only',
  });

  const [updateQuestionnaireAnswers, { loading: saving }] = useMutation<
    { updateQuestionnaireAnswers: IQuestionnaireCompanyResponse },
    { id: string; inputs: IUpdateQuestionnaireAnswersInput[] }
  >(UPDATE_QUESTIONNAIRE_ANSWERS);

  const [submitting, setSubmitting] = useState(false);

  // State to track the latest mutation attempt and prevent concurrent mutations
  const [lastMutationTime, setLastMutationTime] = useState<number>(0);

  const questionnaire = data?.questionnaire;
  const questionnaireResponse = data?.questionnaireCompanyResponse;
  const partnerRequest = questionnaireResponse?.partnerRequest;

  const handleSave = useCallback(
    async (values: IQuestionnaireFormValues) => {
      // Prevent concurrent mutations by checking if one is already in progress
      if (saving) {
        return;
      }

      // Record the time of this mutation attempt
      const mutationTime = Date.now();
      setLastMutationTime(mutationTime);

      const inputs: IUpdateQuestionnaireAnswersInput[] = Object.values(values.sections).flatMap(
        ({ questions }) =>
          Object.entries(questions).flatMap(([id, question]) => ({
            questionId: id,
            ...omit(question, ['selectedOptionId', 'documents']),
            ...('documents' in question
              ? { proofDocumentIds: question.documents?.map(({ id }) => id) }
              : {}),
            // Because the BE expects all selected options in array structure we need to transform the values from the single selects
            ...('selectedOptionId' in question
              ? {
                  selectedOptionIds: question.selectedOptionId ? [question.selectedOptionId] : [],
                }
              : {}),
          }))
      );

      if (!questionnaireResponseId) {
        captureMessage(
          'No questionnaire response id available when trying to save questionnaire answers',
          { level: 'error' }
        );
        return;
      }

      try {
        const result = await updateQuestionnaireAnswers({
          variables: { id: questionnaireResponseId, inputs },
        });

        logEvent('QUESTIONNAIRE_ANSWERS_SAVED');

        // Only process result if this is still the latest mutation attempt
        if (mutationTime >= lastMutationTime) {
          return result;
        }
      } catch (error) {
        // Only handle error if this is still the latest mutation attempt
        if (mutationTime >= lastMutationTime) {
          throw error;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateQuestionnaireAnswers, saving, lastMutationTime]
  );

  const handleFinish = async (values?: IQuestionnaireFormValues) => {
    // Saving is only needed if there are values provided otherwise the form is already up to date
    if (values) {
      await handleSave(values);
    }

    if (!partnerRequest) {
      captureMessage(
        'No partner request linked to questionnaire response, can not fulfill the request',
        { level: 'error' }
      );
      return;
    }
    setSubmitting(true);
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Send self-assessment',
        text: 'Once you send this self-assessment response, your answers can not be edited. Are you sure you want to finish and send?',
        submitText: 'Send self-assessment',
        onSubmit: async () => {
          setSubmitting(true);
          logEvent('PARTNER_REQUEST_PROVIDED', {
            requestType: ERequestType.QUESTIONNAIRE,
          });
          updatePartnerRequest({
            variables: {
              id: partnerRequest.id,
              input: {
                requestStatus: EPartnerRequestStatus.PROVIDED,
              },
            },
            onError: () => {
              setSubmitting(false);
              setErrorMessage(
                'Something went wrong finishing the request. Please try again later.'
              );
            },
            onCompleted: () => {
              setSuccessMessage('You have fulfilled the request');
              navigate('/questionnaires');
            },
          });
        },
        onClose: () => setSubmitting(false),
        onCancel: () => setSubmitting(false),
      },
    });
  };

  return {
    questionnaire,
    questionnaireResponse,
    partnerRequest,
    error,
    loading,
    saving,
    submitting,
    handleSave,
    handleFinish,
  };
};

export default useQuestionnaireAnswerForm;
